import React, { useMemo, useRef, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Mail, Loader2, Eye, PenLine, X, CheckCircle2, Paperclip } from 'lucide-react';
import { Input } from '@/components/ui/input';
import MacroPicker from '@/components/admin/crm/MacroPicker';
import { insertMacroInto, restoreCaret } from '@/lib/crmMacros';

const DEFAULT_SUBJECT = 'A corrected VAT invoice for your recent order — and our apologies';

function composeDefaultBody(firstName) {
  return `Hi ${firstName || 'there'},

Please accept our apologies. When you placed your recent order with us, our checkout incorrectly split it into two separate sales. That meant postage was charged twice and two invoices were issued, when you should have had a single order with one delivery charge.

We've now corrected this. Your purchase is recorded as one sale, with carriage charged once. The duplicate invoice has been cancelled, and your corrected VAT invoice is attached for your records.

The amount you paid is correct, so there is nothing further to pay and no action is needed on your part.

Once again, we're sorry for the confusion — and thank you for your patience.

If you have any questions, simply reply to this email or call us on 0151 528 3001.`;
}

// Admin composer for sending an editable, branded email to a customer from the
// CRM profile (or from an order). Optionally attaches the selected order's VAT
// invoice PDF; the backend logs every send to the customer's timeline.
export default function CustomerEmailComposer({
  customerEmail,
  customerName,
  orders = [],
  defaultOrderId,
  defaultSubject,
  defaultBody,
  onClose,
  onSent,
}) {
  const firstName = (customerName || '').trim().split(/\s+/)[0] || '';
  const sortedOrders = useMemo(
    () => [...orders].sort((a, b) => new Date(b.created_date || 0) - new Date(a.created_date || 0)),
    [orders]
  );

  const [subject, setSubject] = useState(defaultSubject ?? DEFAULT_SUBJECT);
  const [body, setBody] = useState(() => defaultBody ?? composeDefaultBody(firstName));
  const [orderId, setOrderId] = useState(defaultOrderId || sortedOrders[0]?.id || '');
  const [preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const bodyRef = useRef(null);

  const selectedOrder = sortedOrders.find((o) => o.id === orderId) || null;

  const insertMacro = (text, macro) => {
    const { next, caret } = insertMacroInto(bodyRef.current, text);
    setBody(next);
    restoreCaret(bodyRef.current, caret);
    if (macro.subject && !subject.trim()) setSubject(macro.subject);
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      setError('Subject and message are required.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await base44.functions.invoke('sendCustomerEmail', {
        customerEmail,
        customerName,
        subject: subject.trim(),
        body,
        orderId: orderId || undefined,
      });
      if (res.data?.error) throw new Error(res.data.error);
      setSent(true);
      if (onSent) onSent();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to send email.');
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="border border-green-400/20 bg-green-400/5 px-5 py-4 flex items-center gap-3">
        <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
        <p className="text-green-400 text-xs">
          Email sent to {customerEmail} and logged to the customer's timeline.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-white/10 bg-[#0d0d0f]">
      <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-white/5">
        <div className="flex items-center gap-2 min-w-0">
          <Mail className="w-4 h-4 text-primary shrink-0" />
          <p className="text-white text-xs font-bold tracking-widest truncate">
            EMAIL {customerName || customerEmail}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setPreview((p) => !p)}
            className="inline-flex items-center gap-1.5 border border-white/10 hover:border-primary/50 text-white/50 hover:text-white text-[10px] font-bold tracking-wider px-3 py-1.5 transition-colors"
          >
            {preview ? <PenLine className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {preview ? 'EDIT' : 'PREVIEW'}
          </button>
          {onClose && (
            <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {preview ? (
        <div className="p-5">
          <div className="border border-white/10 bg-black">
            <div className="h-[3px] bg-primary" />
            <div className="p-6">
              <p className="text-primary text-[10px] font-bold tracking-[0.15em] mb-3">
                A MESSAGE FROM MOTION REALITY
              </p>
              <p className="text-white font-black text-xl uppercase mb-4 leading-tight">{subject}</p>
              {body.split(/\n{2,}/).map((para, i) => (
                <p key={i} className="text-white/70 text-sm leading-relaxed mb-4 whitespace-pre-wrap">
                  {para}
                </p>
              ))}
              <p className="text-white/70 text-sm mb-5">— The Motion Reality Team</p>
              {selectedOrder && (
                <div className="border border-primary/30 bg-white/[0.02] px-4 py-3 flex items-center gap-2">
                  <Paperclip className="w-3.5 h-3.5 text-primary shrink-0" />
                  <p className="text-white/50 text-xs">
                    Attached: invoice-{selectedOrder.invoice_number || 'pending'}.pdf
                  </p>
                </div>
              )}
            </div>
            <div className="border-t border-white/10 px-6 py-4">
              <p className="text-white/30 text-[11px]">
                Motion Reality Sim Racing Ltd · Unit 9, Croft Business Park, Bromborough, Wirral CH62 3RA
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-5 space-y-4">
          <div>
            <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">SUBJECT</p>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-[#111115] border-white/10 text-white h-10 placeholder:text-white/20 focus:border-primary/60"
            />
            <p className="text-white/25 text-[10px] mt-2">
              A short reference code is added to the subject automatically, and the customer's reply comes straight back to the Email Inbox.
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-white/30 text-[10px] font-bold tracking-widest">MESSAGE</p>
            <MacroPicker
              channel="email"
              context={{ customer_name: customerName }}
              onInsert={insertMacro}
            />
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              className="seo-input resize-none leading-relaxed"
            />
          </div>

          <div>
            <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">ATTACH INVOICE</p>
            <select
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              className="seo-input"
            >
              <option value="">No attachment</option>
              {sortedOrders.map((o) => (
                <option key={o.id} value={o.id}>
                  {(o.product_name || 'Order')} — £{Number(o.amount || 0).toFixed(2)}
                  {o.invoice_number ? ` · ${o.invoice_number}` : ''}
                </option>
              ))}
            </select>
            {selectedOrder && (
              <p className="text-white/30 text-[10px] mt-2 flex items-center gap-1.5">
                <Paperclip className="w-3 h-3 text-primary" />
                Invoice {selectedOrder.invoice_number || '(a number will be assigned)'} will be attached as a PDF.
              </p>
            )}
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          <button
            onClick={handleSend}
            disabled={sending}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {sending ? 'SENDING...' : 'SEND EMAIL'}
          </button>
        </div>
      )}
    </div>
  );
}